import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, Users, Globe, Sparkles } from 'lucide-react';
import { opportunityData } from '../mockData';
import { Button } from '../components/ui/button';

const Opportunity = () => {
  return (
    <div className="min-h-screen pt-20">
      <Helmet>
        <title>The Opportunity | Luxury Glamping Market Growth South Africa</title>
        <meta name="description" content="SA luxury travel market growing at 10.1% annually. Eco-luxury glamping niche at 11.5%. Position your land in the 'Goldilocks Window' for maximum returns." />
        <link rel="canonical" href="https://goglamping.co.za/opportunity" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={opportunityData.image}
            alt="Market opportunity"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-stone-900/90 via-stone-900/80 to-stone-900/90" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              {opportunityData.title}
            </h1>
            <p className="text-2xl text-sage-300 font-semibold mb-6">
              {opportunityData.subtitle}
            </p>
          </div>

          {/* Market Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <div className="bg-white/10 backdrop-blur-md p-6 rounded-xl border border-white/20">
              <div className="text-4xl font-bold text-sage-400 mb-2">
                {opportunityData.marketStats.marketSize}
              </div>
              <div className="text-sm text-gray-300">SA Luxury Travel Market by {opportunityData.marketStats.year}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md p-6 rounded-xl border border-white/20">
              <div className="text-4xl font-bold text-sage-400 mb-2">
                {opportunityData.marketStats.growthRate}
              </div>
              <div className="text-sm text-gray-300">Annual Growth Rate (CAGR)</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md p-6 rounded-xl border border-white/20">
              <div className="text-4xl font-bold text-sage-400 mb-2">
                {opportunityData.marketStats.nicheGrowth}
              </div>
              <div className="text-sm text-gray-300">Eco-Luxury Niche Growth</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md p-6 rounded-xl border border-white/20">
              <div className="text-4xl font-bold text-sage-400 mb-2">
                {opportunityData.financials.projectedROI}
              </div>
              <div className="text-sm text-gray-300">Projected ROI</div>
            </div>
          </div>
        </div>
      </section>

      {/* Financial Brief CTA Banner */}
      <section className="py-12 bg-sage-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-2xl md:text-3xl text-white font-semibold">
            Drop us an email and request The Financial Feasibility Brief
          </p>
        </div>
      </section>

      {/* Why Now Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-stone-900 mb-6">
              The "Why Now" Frame
            </h2>
            <p className="text-xl text-stone-600 leading-relaxed">
              The South African luxury travel market isn't recovering; it's being redefined. While traditional hotels fight over the 5% recovery of mass tourism, the eco-luxury and glamping niche is exploding at 11.5% annually.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="bg-sage-700 rounded-full p-3">
                  <TrendingUp className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-stone-900 mb-2">
                    The 5-Year Goldilocks Window
                  </h3>
                  <p className="text-stone-600 leading-relaxed">
                    High-net-worth travelers from the US, UK, and Europe are moving away from overcrowded luxury hubs like Cape Town's Waterfront and seeking off-the-beaten-track authenticity.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-sage-700 rounded-full p-3">
                  <Sparkles className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-stone-900 mb-2">
                    Exclusivity is the New Gold
                  </h3>
                  <p className="text-stone-600 leading-relaxed">
                    Your land is exactly what they're looking for. If you wait, you're losing a 10.1% annual growth opportunity.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-sage-50 to-stone-100 p-8 rounded-2xl">
              <h3 className="text-2xl font-bold text-stone-900 mb-6">Financial Snapshot</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-4 border-b border-stone-300">
                  <span className="text-stone-700 font-medium">Annual Revenue Per Unit</span>
                  <span className="text-2xl font-bold text-sage-700">{opportunityData.financials.revenuePerUnit}</span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-stone-300">
                  <span className="text-stone-700 font-medium">Peak Season Rate</span>
                  <span className="text-2xl font-bold text-sage-700">{opportunityData.financials.peakRate}/night</span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-stone-300">
                  <span className="text-stone-700 font-medium">Target Occupancy</span>
                  <span className="text-2xl font-bold text-sage-700">{opportunityData.financials.occupancyTarget}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-stone-700 font-medium">Projected ROI</span>
                  <span className="text-3xl font-bold text-sage-700">{opportunityData.financials.projectedROI}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Target Market */}
      <section className="py-24 bg-stone-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              The "Conscious Connoisseur"
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Your customers aren't campers—they're high-value patrons seeking transformation
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-xl border border-white/20">
              <Users className="h-12 w-12 text-sage-400 mb-4" />
              <h3 className="text-2xl font-bold mb-4">Demographics</h3>
              <p className="text-gray-300 mb-2">
                <strong className="text-white">{opportunityData.demographics.ageRange}</strong> - The fastest-growing luxury segment
              </p>
              <p className="text-gray-300">
                {opportunityData.demographics.profile}
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-xl border border-white/20">
              <Globe className="h-12 w-12 text-sage-400 mb-4" />
              <h3 className="text-2xl font-bold mb-4">Spending Power</h3>
              <p className="text-gray-300 mb-2">
                <strong className="text-white">{opportunityData.demographics.spending}</strong> will soon be Glamping
              </p>
              <p className="text-gray-300">
                Affluent travelers prioritizing wellness and regenerative experiences
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-xl border border-white/20">
              <Sparkles className="h-12 w-12 text-sage-400 mb-4" />
              <h3 className="text-2xl font-bold mb-4">The Psyche</h3>
              <p className="text-gray-300 mb-2">
                Suffering from <strong className="text-white">digital fatigue</strong>
              </p>
              <p className="text-gray-300">
                They dont always want a TV; they want a 180° mountain view through high-performance membrane walls
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 5-10 Year Trend */}
      <section className="py-24 bg-gradient-to-br from-sage-50 to-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-stone-900 mb-6">
              From "Glamping" to "Architectural Sanctuaries"
            </h2>
            <p className="text-xl text-stone-600 max-w-3xl mx-auto">
              The trend is moving from canvas tents to high-tech pods. Here's the market evolution:
            </p>
          </div>

          <div className="space-y-6">
            {opportunityData.trends.map((trend) => (
              <div
                key={trend.id}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                  <div className="mb-4 md:mb-0">
                    <div className="text-sage-700 font-bold text-lg mb-2">{trend.period}</div>
                    <h3 className="text-2xl font-bold text-stone-900">{trend.phase}</h3>
                  </div>
                  <p className="text-stone-600 md:max-w-md">{trend.demand}</p>
                </div>
              </div>
            ))}

            <div className="bg-sage-700 text-white p-8 rounded-2xl shadow-lg">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div className="mb-4 md:mb-0">
                  <div className="text-sage-300 font-bold text-lg mb-2">By 2030</div>
                  <h3 className="text-2xl font-bold">The Sustainability Mandate</h3>
                </div>
                <p className="md:max-w-md">
                  Luxury will be synonymous with 'Zero-Impact.' Because our offering uses no permanent concrete and is 100% removable, your land remains a pristine investment while generating cash.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-sage-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Download the Financial Feasibility Brief
          </h2>
          <p className="text-xl mb-8 text-sage-100">
            Get detailed projections, market analysis, and ROI calculations for your property
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-sage-700 hover:bg-gray-100 px-8 py-6 text-lg rounded-lg shadow-2xl hover:scale-105 transition-all duration-300">
              Request Your Feasibility Brief
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Opportunity;
